import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, ShieldCheck } from "lucide-react";
import { HitmasterLogo } from "@/components/HitmasterLogo";
import { Equalizer } from "@/components/Equalizer";
import { BluetoothGuide } from "@/components/BluetoothGuide";
import { StyleSelector } from "@/components/StyleSelector";
import { FlagSwitcher } from "@/components/FlagSwitcher";
import { useLang } from "@/i18n";
import { useAuth } from "@/auth/AuthContext";
import { THEMES, getTheme, getStoredStyle, setStoredStyle, bgImageStyle } from "@/lib/themes";

export default function Landing() {
  const nav = useNavigate();
  const { t } = useLang();
  const { user, logout } = useAuth();
  const [styleId, setStyleId] = useState(getStoredStyle());
  const theme = getTheme(styleId);
  const pickStyle = (id) => { setStyleId(id); setStoredStyle(id); };
  const doLogout = () => { logout(); nav("/login", { replace: true }); };
  return (
    <div
      className="min-h-screen landing-bg flex flex-col items-center px-6 pb-10 relative overflow-hidden"
      style={{ paddingTop: "10px", ...(theme.landing.type === "image" ? bgImageStyle(theme.landing.url) : {}) }}
    >
      {/* Auth controls (top-right) */}
      <div className="fixed top-3 right-3 z-30 flex items-center gap-2">
        {user?.role === "admin" && (
          <button
            data-testid="landing-admin-link"
            onClick={() => nav("/admin")}
            className="flex items-center gap-1.5 text-xs font-semibold text-white bg-amber-500/80 hover:bg-amber-500 px-3 py-1.5 rounded-full backdrop-blur-sm"
          >
            <ShieldCheck className="w-3.5 h-3.5" /> Admin
          </button>
        )}
        <button
          data-testid="landing-logout"
          onClick={doLogout}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-black/40 hover:bg-black/60 px-3 py-1.5 rounded-full backdrop-blur-sm"
        >
          <LogOut className="w-3.5 h-3.5" /> Uitloggen
        </button>
      </div>

      {theme.landing.type === "video" && (
      <>
      {/* Looping video background — themed first-screen clip */}
      <video
        data-testid="landing-bg-video"
        autoPlay
        loop
        muted
        playsInline
        preload="auto"
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: 0,
          pointerEvents: "none",
          filter: "saturate(1.22) contrast(1.06) brightness(1.03)",
        }}
        src={theme.landing.url}
      />
      </>
      )}
      {/* Readability overlay: vertical darkening (strong behind logo/tagline top
          and buttons/footer bottom, lighter in the middle) + soft edge vignette. */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(14, 18, 40, 0.28)",
          backdropFilter: "blur(5px)",
          WebkitBackdropFilter: "blur(5px)",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      <div className="relative flex flex-col items-center w-full" style={{ zIndex: 1 }}>
      <HitmasterLogo className="h-32 sm:h-44 animate-float" />
      <div className="mt-4 sm:mt-5" data-testid="landing-flags">
        <FlagSwitcher />
      </div>
      <div className="flex flex-col items-center gap-8 max-w-2xl w-full text-center mt-10 sm:mt-14">

        <p className="leading-tight">
          <span
            data-testid="landing-tagline-groovy"
            className="block font-bebas text-white text-white-shadow text-4xl sm:text-6xl leading-none mb-3"
          >
            {t("landing.tagline1")}
          </span>
          <span
            data-testid="landing-tagline-gunslinger"
            className="block font-bebas text-white text-white-shadow text-2xl sm:text-4xl leading-tight"
          >
            {t("landing.tagline2")}
          </span>
        </p>

        <div className="flex items-center justify-center text-gold" data-testid="landing-audio-bars">
          <Equalizer />
        </div>

        <button
          data-testid="landing-host-btn"
          onClick={() => nav("/host")}
          className="hover:brightness-110 transition p-4 sm:p-5 group w-full max-w-md text-left backdrop-blur-xl rounded-2xl"
          style={{
            border: "4px solid #FFF9EE",
            backgroundColor: "rgba(73, 102, 161, 0.55)",
            boxShadow:
              "0 18px 40px rgba(0,0,0,0.30), 0 8px 16px rgba(0,0,0,0.22), inset 0 3px 8px rgba(255,255,255,0.45), inset 0 -6px 16px rgba(0,0,0,0.32)",
          }}
        >
          <div className="font-display font-black text-cream text-xl sm:text-2xl mb-1 drop-shadow-[0_2px_0_rgba(0,0,0,0.4)]">{t("landing.hostTitle")}</div>
          <div className="text-cream/85 font-bold text-sm drop-shadow-[0_1px_0_rgba(0,0,0,0.3)]">{t("landing.hostDesc")}</div>
          <div className="mt-3 inline-block bg-coral text-white font-display font-black uppercase text-sm px-5 py-2 rounded-full border-2 border-cream shadow-[0_10px_24px_rgba(0,0,0,0.45)] group-hover:translate-y-[-2px] transition-transform">
            {t("landing.hostCta")}
          </div>
        </button>

        <div className="w-full max-w-md flex justify-center" data-testid="landing-bluetooth-wrap">
          <BluetoothGuide />
        </div>

        <StyleSelector selected={styleId} onSelect={pickStyle} />
      </div>

      <div className="mt-16 flex flex-col items-center gap-1">
        <a
          href="https://www.hitmaster.eu"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="footer-hitmaster-link"
          className="text-white font-light tracking-widest uppercase underline decoration-white/50 hover:decoration-white transition-colors"
          style={{ fontSize: "0.72rem", textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}
        >
          www.hitmaster.eu
        </a>
        <div
          className="text-white font-light tracking-widest uppercase"
          style={{ fontSize: "0.68rem", textShadow: "0 2px 6px rgba(0,0,0,0.6), 0 1px 0 rgba(0,0,0,0.4)" }}
          data-testid="landing-footer"
        >
          {t("landing.footer")}
        </div>
      </div>
      </div>
    </div>
  );
}
